// Pure data — no icon component values, so this stays server-safe.

export const QUICK_ANSWER =
  "Webshare doesn't sell mobile proxies at all, so if you specifically need premium pooled mobile IPs, Proxidize is the direct fit. For residential proxies, Proxidize's flat $1/GB from 25 GB beats every Webshare rotating residential tier we checked. Webshare wins on a permanent free datacenter tier, cheap static ISP proxies, and very small residential tests under 25 GB.";

export const KEY_TAKEAWAYS = [
  "Webshare's public lineup doesn't include mobile proxies. Its own FAQ confirms mobile isn't currently offered.",
  "Proxidize residential is cheaper at every volume Webshare charges for. $100 vs $225 at 100 GB/month, for example.",
  "Webshare has a permanent free tier. 10 datacenter proxies and 1 GB/month, no credit card, no expiry — Proxidize has no free tier, only free sign-up.",
  "Webshare sells static ISP proxies you keep for the plan's life. From $0.30/proxy — a category Proxidize doesn't yet offer.",
  "Webshare uses risk-based verification, not required KYC. Proxidize requires KYC from every customer after purchase, before they can use the proxies.",
  "Webshare's smallest residential test is well below Proxidize's minimum. 1 GB for $3.50 versus Proxidize's 25 GB floor.",
];

export interface VerdictCard {
  provider: "proxidize" | "webshare";
  title: string;
  points: string[];
}

export const VERDICT_CARDS: VerdictCard[] = [
  {
    provider: "proxidize",
    title: "Choose Proxidize if you need",
    points: [
      "Premium pooled mobile IPs — a product category Webshare's current lineup doesn't include at all",
      "Residential proxies from a flat $1/GB starting at 25 GB, with no minimum spend and unused bandwidth that rolls over",
      "Residential ISP targeting alongside country and city targeting",
      "Ethically sourced, fully compliant mobile and residential networks",
      "A 30-day evaluation window, conditional on staying under 10 GB of usage",
    ],
  },
  {
    provider: "webshare",
    title: "Choose Webshare if you need",
    points: [
      "A permanent free plan: 10 datacenter proxies and 1 GB/month, no credit card required",
      "A very small residential test purchase — plans start at 1 GB for $3.50",
      "Static residential (ISP) proxies you keep for the life of the plan, from $0.30/proxy",
      "Low-cost datacenter proxies, from about $0.0299/proxy",
      "Lighter, risk-based verification instead of Proxidize's required KYC on every account",
    ],
  },
];

export interface ComparisonRow {
  feature: string;
  proxidize: string | boolean;
  webshare: string | boolean;
}

export const COMPARISON_ROWS: ComparisonRow[] = [
  {
    feature: "Best for",
    proxidize: "Premium pooled mobile IPs and lower-cost residential bandwidth at volume",
    webshare: "Free datacenter proxies, static ISP proxies, and very small residential tests",
  },
  { feature: "Mobile proxies offered", proxidize: true, webshare: false },
  {
    feature: "Product lineup",
    proxidize: "Mobile and residential live; ISP and datacenter coming soon",
    webshare: "Rotating residential, static residential (ISP), and datacenter proxies",
  },
  {
    feature: "Mobile pricing",
    proxidize: "Flat $2/GB, no monthly minimum",
    webshare: "Not offered as a public product",
  },
  {
    feature: "Rotating residential network",
    proxidize: "Millions of IPs, 195+ countries, ethically sourced",
    webshare: "80M+ IPs across 195+ countries and cities",
  },
  {
    feature: "Rotating residential pricing model",
    proxidize: "Flat $1/GB from 25 GB",
    webshare: "Volume-tiered per-GB rate, from $3.50/GB at 1 GB down to $1.40/GB at 3,000 GB",
  },
  {
    feature: "Smallest rotating residential purchase",
    proxidize: "25 GB for $25",
    webshare: "1 GB for $3.50",
  },
  {
    feature: "Rotating residential price at 25 GB",
    proxidize: "$25 ($1.00/GB)",
    webshare: "$65 ($2.60/GB)",
  },
  {
    feature: "Rotating residential price at 100 GB",
    proxidize: "$100 ($1.00/GB)",
    webshare: "$225 ($2.25/GB)",
  },
  {
    feature: "Rotating residential price at 1,000 GB",
    proxidize: "$1,000 ($1.00/GB)",
    webshare: "$1,500 ($1.50/GB)",
  },
  {
    feature: "Unused rotating residential bandwidth",
    proxidize: "Rolls over, doesn't expire",
    webshare: "Not clearly published for normal renewals — see our note below the calculator",
  },
  {
    feature: "Rotating residential targeting",
    proxidize: "Country, city, and ISP targeting",
    webshare: "Country and city targeting (username-based)",
  },
  {
    feature: "Sessions",
    proxidize: "Sticky or random per access point",
    webshare: "Rotating or sticky sessions via the endpoint generator",
  },
  {
    feature: "Protocols",
    proxidize: "HTTP(S), SOCKS5, HTTP/3 + QUIC",
    webshare: "HTTP(S), SOCKS5",
  },
  {
    feature: "Concurrency",
    proxidize: "Unlimited, published",
    webshare: "Not publicly quantified",
  },
  {
    feature: "Static ISP (static residential) proxies",
    proxidize: "Not yet offered",
    webshare: "Yes, from $0.30/proxy, shared/private/dedicated tiers",
  },
  {
    feature: "Datacenter proxies",
    proxidize: "Not yet offered",
    webshare: "Yes, from about $0.0299/proxy, plus a permanent free tier",
  },
  {
    feature: "Free tier",
    proxidize: "No free tier — free sign-up with no credit card",
    webshare: "10 free datacenter proxies + 1 GB/month, no credit card, no expiry",
  },
  {
    feature: "KYC",
    proxidize: "Required after purchase, before you can use the proxies",
    webshare: "Risk-based — not required unless flagged",
  },
  {
    feature: "Refund window",
    proxidize: "30 days, conditional on under 10 GB usage",
    webshare: "48 hours, conditional on under 0.1–1 GB usage depending on product",
  },
];

export interface UseCaseFit {
  useCase: string;
  fit: "Proxidize" | "Webshare" | "Both";
  reason: string;
}

export const USE_CASE_FITS: UseCaseFit[] = [
  {
    useCase: "Premium cellular mobile IPs",
    fit: "Proxidize",
    reason:
      "Webshare's current public lineup doesn't include mobile proxies at all, so this isn't a close call.",
  },
  {
    useCase: "Residential traffic from 25 GB to 3,000+ GB per month",
    fit: "Proxidize",
    reason:
      "Proxidize's flat $1/GB beats every Webshare residential tier we checked at directly comparable monthly volumes — $100 vs $225 at 100 GB, for example.",
  },
  {
    useCase: "A tiny, low-cost residential test",
    fit: "Webshare",
    reason:
      "Webshare's smallest residential plan starts at 1 GB for $3.50, below Proxidize's 25 GB minimum.",
  },
  {
    useCase: "Zero-cost proxy testing with no card on file",
    fit: "Webshare",
    reason:
      "Webshare's free plan (10 datacenter proxies, 1 GB/month) is permanent and needs no credit card; Proxidize has no free tier, only free sign-up.",
  },
  {
    useCase: "A stable, static IP identity you keep long-term",
    fit: "Webshare",
    reason:
      "Webshare's static residential (ISP) proxies hold the same IP for the life of the plan; Proxidize doesn't publicly offer a static/ISP product yet.",
  },
  {
    useCase: "Low-cost datacenter proxies at scale",
    fit: "Webshare",
    reason:
      "Webshare publishes dedicated datacenter pricing from about $0.0299/proxy; Proxidize has no datacenter product.",
  },
  {
    useCase: "Residential ISP-level targeting",
    fit: "Proxidize",
    reason:
      "Proxidize documents country, city, and ISP targeting; Webshare's rotating residential targeting is country and city only.",
  },
  {
    useCase: "Avoiding identity verification altogether",
    fit: "Webshare",
    reason:
      "Webshare uses risk-based verification and doesn't require KYC unless activity looks suspicious; Proxidize requires KYC from every customer after purchase, before they can use the proxies.",
  },
  {
    useCase: "A longer window to evaluate before committing",
    fit: "Proxidize",
    reason: "Proxidize gives 30 days (under 10 GB usage) versus Webshare's 48-hour refund window.",
  },
];

export interface MigrationStep {
  title: string;
  description: string;
}

export const MIGRATION_STEPS: MigrationStep[] = [
  {
    title: "Create a free account",
    description: "Sign up for free: no credit card, no sales call, no monthly minimum.",
  },
  {
    title: "Choose your per-GB residential plan",
    description:
      "Pick the monthly data volume you need at a flat $1/GB, with plans from 25 GB to 10,000 GB and no contract.",
  },
  {
    title: "Create an access point",
    description:
      "Choose a country, city, or ISP, and set sticky or random IP behavior — the same knobs you tuned in Webshare's endpoint generator.",
  },
  {
    title: "Copy your credentials",
    description:
      "Credentials are generated live in HTTP or SOCKS5 format, with auto-generated cURL examples and formatted proxy list exports.",
  },
  {
    title: "Test before switching traffic",
    description:
      "Run your existing scraper or browser profile against the new endpoint with a small job and compare success rates side by side.",
  },
  {
    title: "Monitor usage in real time",
    description:
      "The built-in analytics dashboard shows bandwidth trends, top domains, and a live connection log per access point.",
  },
];

export const MIGRATION_CURL = `curl -x "http://USERNAME:PASSWORD@HOST:PORT" https://ipinfo.io`;
